import React, { useEffect, useState } from 'react';
import { FaSave } from 'react-icons/fa';

const WeightGroupsTab = () => {
  const [weightGroups, setWeightGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState(null); // Data for Add/Edit modal
  const [deleteId, setDeleteId] = useState(null); // ID of weight group to delete
  const [editMode, setEditMode] = useState(false); // Edit mode toggle
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch weight groups
  const fetchWeightGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/weight-group`);
      if (!response.ok) throw new Error('Failed to fetch weight groups');
      const data = await response.json();
      setWeightGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setWeightGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeightGroups();
  }, []);

  // Save or update a weight group
  const handleSave = async (group) => {
    const method = editMode ? 'PUT' : 'POST';
    const url = editMode
      ? `${apiUrl}/api/weight-group/${group.id}`
      : `${apiUrl}/api/weight-group`;

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(group),
      });
      if (!response.ok) throw new Error('Error saving weight group');
      await fetchWeightGroups();
      setModalData(null); // Close modal
    } catch (err) {
      setError('Error saving weight group. Please try again.');
    }
  };

  // Delete a weight group
  const handleDelete = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/weight-group/${deleteId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Error deleting weight group');
      await fetchWeightGroups();
      setDeleteId(null); // Close delete confirmation modal
    } catch (err) {
      setError('Error deleting weight group. Please try again.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Weight Groups</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditMode(false);
            setModalData({ name: '' }); // Open modal with empty data
          }}
        >
          Add Weight Group
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : weightGroups.length === 0 ? (
        <p>No weight groups available.</p>
      ) : (
        <table className="table w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-base-200">Name</th>
              <th className="border border-gray-300 bg-base-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {weightGroups.map((group) => (
              <tr key={group.id}>
                <td>{group.name}</td>
                <td className="flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditMode(true);
                      setModalData(group); // Open modal with group data
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setDeleteId(group.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add/Edit Modal */}
      {modalData && (
        <dialog className="modal modal-open bg-gray-50">
          <form
            className="modal-box"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave(modalData);
            }}
          >
            <h3 className="font-bold text-lg">
              {editMode ? 'Edit Weight Group' : 'Add Weight Group'}
            </h3>
            <div className="form-control">
              <label htmlFor="name" className="label-text">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={modalData.name || ''}
                onChange={(e) =>
                  setModalData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="input input-bordered"
                required
              />
            </div>
            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => setModalData(null)} // Close modal
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <FaSave />
                {editMode ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirm Deletion</h3>
            <p>Are you sure you want to delete this weight group? This action cannot be undone.</p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setDeleteId(null)} // Close modal
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default WeightGroupsTab;
